import React, { useMemo, useState } from 'react'
import {
	Avatar,
	Box,
	Dialog,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import {
	calendarInputSx,
	calendarPopperSlotProps,
	popupInputLabelSx,
} from '../popupInputStyles'
import { useModal } from '../../../../../store/ModalProvider'
import type { ChapasPorMotoristasModalData, ChapasPorMotoristasRow } from '../../../../../types/modal'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

type DensityMode = 'compact' | 'standard'

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const chapasTableSx = {
	'& .MuiDataGrid-columnHeader': {
		padding: '0 16px',
	},
	'& .MuiDataGrid-cell': {
		padding: '0 16px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.04)',
	},
}

const avatarPalette = ['#00A5FF', '#FF8B2A', '#2E7D32', '#1F4B99', '#F5B350']

const getAvatarColor = (name: string) => {
	const code = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
	return avatarPalette[code % avatarPalette.length]
}

const getInitials = (name: string) =>
	name
		.split(' ')
		.filter(Boolean)
		.slice(0, 2)
		.map(part => part[0])
		.join('')
		.toUpperCase()

const defaultRows: ChapasPorMotoristasRow[] = [
	{
		id: '1',
		motorista: 'Beatriz Silva',
		numMec: '298',
		folga: 'seg',
		compl: 'ter',
		naoEfetuadas: '10',
		seg: '',
		ter: '',
		qua: '0003',
		qui: '0003',
		sex: '0003',
		sab: '0003',
	},
	{
		id: '2',
		motorista: 'Carlos Cardoso',
		numMec: '110',
		folga: 'qui',
		compl: 'sex',
		naoEfetuadas: '4',
		seg: '0016',
		ter: '0003',
		qua: '',
		qui: '',
		sex: '',
		sab: '0003',
	},
	{
		id: '3',
		motorista: 'Rita Madeira',
		numMec: '234',
		folga: 'seg',
		compl: 'ter',
		naoEfetuadas: '3',
		seg: '',
		ter: '',
		qua: '0003',
		qui: '0016',
		sex: '0003',
		sab: '0003',
	},
	{
		id: '4',
		motorista: 'Vasco Silva',
		numMec: '908',
		folga: 'sáb',
		compl: 'dom',
		naoEfetuadas: '2',
		seg: '0016',
		ter: '0003',
		qua: '0016',
		qui: '0016',
		sex: '0003',
		sab: '',
	},
	{
		id: '5',
		motorista: 'Vasco Antunes',
		numMec: '780',
		folga: 'qua',
		compl: 'qui',
		naoEfetuadas: '20',
		seg: '0003',
		ter: '',
		qua: '',
		qui: '',
		sex: '',
		sab: '',
	},
]

const defaultDriverOptions = ['Anabela, Isabel, Rui (+2)', 'Todos']
const defaultSeasonOptions = ['Todas', 'Alta', 'Baixa']
const defaultChapaOptions = ['0003, 0016', '0003', '0016']

const CustomModalChapasPorMotoristas: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as ChapasPorMotoristasModalData | undefined
	const [handlePrint, handleSend] = modalHandlers ?? []

	const rows = useMemo(() => {
		if (data?.rows?.length) {
			return data.rows
		}
		return defaultRows
	}, [data])

	const driverOptions = data?.motoristas?.length && data.motoristas ? data.motoristas : defaultDriverOptions
	const seasonOptions = data?.sazonalidades?.length && data.sazonalidades ? data.sazonalidades : defaultSeasonOptions
	const chapaOptions = data?.chapas?.length && data.chapas ? data.chapas : defaultChapaOptions

	const [startDate, setStartDate] = useState<Dayjs | null>(
		data?.startDate ? dayjs(data.startDate, 'DD/MM/YYYY') : dayjs('01/11/2024'),
	)
	const [endDate, setEndDate] = useState<Dayjs | null>(
		data?.endDate ? dayjs(data.endDate, 'DD/MM/YYYY') : dayjs('30/11/2024'),
	)
	const [driver, setDriver] = useState<string>(data?.motorista ?? driverOptions[0])
	const [season, setSeason] = useState<string>(data?.sazonalidade ?? seasonOptions[0])
	const [chapa, setChapa] = useState<string>(data?.chapa ?? chapaOptions[0])
	const [density, setDensity] = useState<DensityMode>('compact')

	const handleClearFilters = () => {
		setStartDate(dayjs('01/11/2024'))
		setEndDate(dayjs('30/11/2024'))
		setDriver(driverOptions[0])
		setSeason(seasonOptions[0])
		setChapa(chapaOptions[0])
	}

	const handlePrintClick = () => {
		if (handlePrint) {
			handlePrint(rows)
			return
		}
		console.log('Imprimir chapas por motoristas', rows)
	}

	const handleSendClick = () => {
		if (handleSend) {
			handleSend(rows)
			return
		}
		console.log('Enviar chapas por motoristas', rows)
	}

	const columns = useMemo<GridColDef<ChapasPorMotoristasRow>[]>(
		() => [
			{
				field: 'motorista',
				headerName: 'Motorista',
				width: 180,
				renderCell: params => {
					const name = params.value as string
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Avatar
								src={params.row.avatarUrl}
								sx={{
									borderRadius: '100%',
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									// bgcolor: 'lightgrey 50% / cover no-repeat',
									width: 32,
									height: 32,
									fontSize: '20px',
									fontWeight: 400,
									lineHeight: 1,
									bgcolor: getAvatarColor(name),
								}}
							>
								{getInitials(name)}
							</Avatar>
							<Typography
								sx={{
									fontSize: '12px',
									fontWeight: 500,
									color: '#092056',
								}}
							>
								{name}
							</Typography>
						</Box>
					)
				},
			},
			{
				field: 'numMec',
				headerName: 'Num Mec',
				width: 70,
				headerAlign: 'center',
				align: 'center',
			},
			{
				field: 'folga',
				headerName: 'Folga',
				width: 70,
				headerAlign: 'center',
				align: 'center',
			},
			{
				field: 'compl',
				headerName: 'Compl',
				width: 70,
				headerAlign: 'center',
				align: 'center',
			},
			{
				field: 'naoEfetuadas',
				headerName: 'Não Efetuadas',
				width: 110,
				headerAlign: 'center',
				align: 'center',
			},
			{
				field: 'seg',
				headerName: 'Seg - 01 Nov',
				width: 90,
				headerAlign: 'center',
				align: 'center',
				renderCell: params => <Typography sx={{ fontSize: '12px', color: '#1F4B99' }}>{params.value}</Typography>,
			},
			{
				field: 'ter',
				headerName: 'Ter - 02 Nov',
				width: 90,
				headerAlign: 'center',
				align: 'center',
				renderCell: params => <Typography sx={{ fontSize: '12px', color: '#1F4B99' }}>{params.value}</Typography>,
			},
			{
				field: 'qua',
				headerName: 'Qua - 03 Nov',
				width: 90,
				headerAlign: 'center',
				align: 'center',
				renderCell: params => <Typography sx={{ fontSize: '12px', color: '#1F4B99' }}>{params.value}</Typography>,
			},
			{
				field: 'qui',
				headerName: 'Qui - 04 Nov',
				width: 90,
				headerAlign: 'center',
				align: 'center',
				renderCell: params => <Typography sx={{ fontSize: '12px', color: '#1F4B99' }}>{params.value}</Typography>,
			},
			{
				field: 'sex',
				headerName: 'Sex - 05 Nov',
				width: 90,
				headerAlign: 'center',
				align: 'center',
				renderCell: params => <Typography sx={{ fontSize: '12px', color: '#1F4B99' }}>{params.value}</Typography>,
			},
			{
				field: 'sab',
				headerName: 'Sáb - 06 Nov',
				width: 90,
				headerAlign: 'center',
				align: 'center',
				renderCell: params => <Typography sx={{ fontSize: '12px', color: '#1F4B99' }}>{params.value}</Typography>,
			},
		],
		[],
	)

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1080px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box
					sx={{
						padding: '16px 24px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Distribuição Chapas por Motoristas
					</Typography>
					<IconButton
						aria-label='Fechar'
						onClick={closeModal}
						sx={{ padding: 0.5 }}
					>
						<CloseRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
					</IconButton>
				</Box>

				<Box sx={{ padding: '0 24px 24px', display: 'grid', gap: '24px' }}>
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'center',
							gap: '16px',
						}}
					>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label='Data Inicial'
								value={startDate}
								onChange={newValue => setStartDate(newValue)}
								format='DD/MM/YYYY'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { maxWidth: '140px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
							/>
						</LocalizationProvider>
						<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#092056' }}>
							{String.fromCharCode(0x2014)}
						</Typography>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label='Data Final'
								value={endDate}
								onChange={newValue => setEndDate(newValue)}
								format='DD/MM/YYYY'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { maxWidth: '140px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
							/>
						</LocalizationProvider>
						<TextField
							select
							label='Motorista'
							value={driver}
							onChange={event => setDriver(event.target.value)}
							size='small'
							sx={{ ...inputSx, minWidth: '200px' }}
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
						>
							{driverOptions.map(option => (
								<MenuItem
									key={option}
									value={option}
								>
									{option}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							label='Sazonalidade'
							value={season}
							onChange={event => setSeason(event.target.value)}
							size='small'
							sx={{ ...inputSx, minWidth: '140px' }}
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
						>
							{seasonOptions.map(option => (
								<MenuItem
									key={option}
									value={option}
								>
									{option}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							label='Chapa'
							value={chapa}
							onChange={event => setChapa(event.target.value)}
							size='small'
							sx={{ ...inputSx, minWidth: '140px' }}
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
						>
							{chapaOptions.map(option => (
								<MenuItem
									key={option}
									value={option}
								>
									{option}
								</MenuItem>
							))}
						</TextField>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleClearFilters}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '12px',
								textTransform: 'none',
								marginLeft: 'auto',
								padding: '4px 0',
								'&:hover': { backgroundColor: 'transparent' },
							}}
						>
							Limpar Filtros
						</CustomButton>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
							gap: '16px',
							minHeight: '32px',
						}}
					>
						<Typography
							sx={{
								fontSize: '20px',
								fontWeight: 600,
								lineHeight: 1.6,
								color: '#092056',
							}}
						>
							Chapas por Dias
						</Typography>
						<ToggleButtonGroup
							value={density}
							exclusive
							onChange={(_, value) => value && setDensity(value)}
							size='small'
							sx={toggleGroupSx}
						>
							<ToggleButton
								value='compact'
								aria-label='densidade compacta'
							>
								<TableRowsIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
							<ToggleButton
								value='standard'
								aria-label='densidade espaçada'
							>
								<ViewHeadlineIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<AnimatedDataGridPro
						slots={{ columnMenu: CustomColumnMenu }}
						slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
						rows={rows}
						columns={columns}
						density={density}
						rowHeight={density === 'compact' ? 32 : 40}
						columnHeaderHeight={40}
						autoHeight
						disableRowSelectionOnClick
						hideFooter
						sx={{
							...tableSx,
							...chapasTableSx,
							...dataGridDensityTransitionSx,
						}}
					/>

					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: '16px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={closeModal}
							sx={popupSecondaryButtonSx}
						>
							Fechar
						</CustomButton>
						<CustomButton
							variant='text'
							size='small'
							onClick={handlePrintClick}
							sx={popupSecondaryButtonSx}
						>
							Imprimir
						</CustomButton>
						<CustomButton
							variant='contained'
							size='small'
							onClick={handleSendClick}
							sx={{ ...popupActionButtonSx, minWidth: '96px' }}
						>
							Enviar
						</CustomButton>
					</Box>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalChapasPorMotoristas
